import axios, { AxiosError, AxiosInstance } from 'axios'
import { env } from '@/lib/env'
import { getAccessToken } from '@/stores/auth-store'
import type { ApiError, ProblemDetail } from './types'

const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: env.VITE_API_BASE_URL,
    withCredentials: false,
  })

  instance.interceptors.request.use((config) => {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ProblemDetail>) => {
      if (error.response?.status === 401) {
        // OIDC's automaticSilentRenew should handle most token refreshes; an
        // unrecoverable 401 means the user needs to re-auth via /login.
        window.dispatchEvent(new CustomEvent('auth:expired'))
      }

      const problemDetail = error.response?.data
      const apiError: ApiError = {
        status: error.response?.status || 500,
        detail: problemDetail?.detail || error.message || 'An error occurred',
        title: problemDetail?.title || 'Error',
      }

      return Promise.reject(apiError)
    }
  )

  return instance
}

export const apiClient = createAxiosInstance()
